import { useArtistContext } from "Components/ArtistProvider";
import { Modal } from "Components/Modal";
import { getFirestore } from "firebase/firestore";
import { doc } from "firebase/firestore";
import React, { useLayoutEffect, useState } from "react";
import { useDocumentData } from "react-firebase-hooks/firestore";
import styled from "styled-components";
import { ArtistSubdomainModal } from "Components/ArtistSubdomainModal";
import { ReactComponent as LinkIcon } from "assets/svg/link-alt.svg";
import { useMobileMediaQuery } from "hooks";
import { Body1 } from "melodies-source/Text";
import { ModalHeaderTitle } from "melodies-source/Modal";
import { ReactComponent as LinkCampaignSvg } from "assets/svg/set-fan-link-campaign.svg";
import { PublishOption } from "./PublishOption";
import { ArtistImage } from "./ArtistImage";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  surveyId: string;
  subdomain: string | undefined;
  handlePublish: () => void;
  handlePublishSubdomain: () => void;
  featuredSurveyId: string | undefined;
}

export const PublishModal: React.FC<Props> = ({
  isOpen,
  subdomain,
  onClose,
  surveyId,
  handlePublish,
  handlePublishSubdomain,
  featuredSurveyId,
}) => {
  const [value, setValue] = useState(false);
  const [currentSurvey] = useDocumentData(
    doc(getFirestore(), `sts3_surveys/${featuredSurveyId}`),
  );
  const [loading, setLoading] = useState(false);
  const [isSubdomainModalOpen, setIsSubdomainModalOpen] = useState(false);
  const artist = useArtistContext();
  const isMobile = useMobileMediaQuery();

  useLayoutEffect(() => {
    setValue(surveyId === featuredSurveyId);
  }, [surveyId, featuredSurveyId]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        withCloseIcon={false}
        actions={{
          main: {
            text: "Publish",
            loading,
            onClick: async () => {
              setLoading(true);
              await (value ? handlePublishSubdomain() : handlePublish());
              setLoading(false);
            },
          },
          secondary: {
            text: "Cancel",
            onClick: onClose,
          },
        }}
        style={isMobile && { padding: 20 }}
        withBackdropClose={!isSubdomainModalOpen}
      >
        <Container>
          <HeaderContainer>
            <LinkCampaignSvg />
            <ModalHeaderTitle>
              How do you want to link to this campaign?
            </ModalHeaderTitle>
          </HeaderContainer>
          <Description>
            You can use the dedicated link that is unique to this campaign, or
            you can choose to feature this campaign at your artist subdomain.
          </Description>
          <PublishOption
            isSelected={!value}
            onClick={() => setValue(false)}
            image={
              <PublishModalLinkImageContainer>
                <LinkIcon />
              </PublishModalLinkImageContainer>
            }
            title="Use dedicated campaign link"
            subtitle={`set.fan/${surveyId}`}
          />
          <PublishOption
            isSelected={value}
            onClick={() =>
              subdomain ? setValue(true) : setIsSubdomainModalOpen(true)
            }
            image={<ArtistImage imageUrl={artist?.assets.icon?.path} />}
            title={
              subdomain
                ? "Use your SET.Fan subdomain"
                : "Create your artist subdomain"
            }
            subtitle={subdomain ? `${subdomain}.set.fan` : undefined}
          />
          {currentSurvey && value && surveyId !== featuredSurveyId && (
            <CurrentSurveyMessage>
              <Body1>
                {subdomain}.set.fan is currently pointing to your{" "}
                <b>{currentSurvey?.title}</b> campaign.
              </Body1>
              <Body1>
                Choosing this option will point {subdomain}.set.fan to this
                survey instead. The previous survey will still be accessible via
                its unique link.
              </Body1>
            </CurrentSurveyMessage>
          )}
        </Container>
      </Modal>
      <ArtistSubdomainModal
        isOpen={isSubdomainModalOpen}
        onClose={() => setIsSubdomainModalOpen(false)}
        mode="create"
      />
    </>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  svg {
    height: 100px;
  }

  ${ModalHeaderTitle} {
    color: var(--main-color);
    text-align: center;
  }
`;

const Description = styled(Body1)`
  text-align: center;

  ${({ theme }) => theme.media.mobile} {
    font-size: 14px;
  }
`;

const PublishModalLinkImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  border-radius: 50%;
  background-color: var(--main-color);
  width: 50px;
  height: 50px;
  transform: rotate(90deg);

  svg {
    width: 20px;
    height: 20px;
  }

  ${({ theme }) => theme.media.mobile} {
    width: 45px;
    height: 45px;
  }
`;

const CurrentSurveyMessage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 10px;
  background: var(--content-background-color);
  padding: 25px;

  ${Body1} {
    color: var(--secondary-text-color);
    font-size: 14px;

    ${({ theme }) => theme.media.mobile} {
      font-size: 12px;
    }
  }

  ${({ theme }) => theme.media.mobile} {
    padding: 20px;

    ${Body1} {
      font-size: 12px;
    }
  }
`;
